import { __decorate, __metadata } from 'tslib';
import { Inject, InjectorService } from '@tencent/ioc';
import { nameOf } from '@tencent/workbench-core';
import { PreHandlers } from '../../registries/pre-handler-registry.js';
import { ContextService } from '../../services/context/context-service.js';
import { ControllerRegistry } from '../../registries/controller-registry.js';
import { MiddlewareRegistry } from '../../registries/middleware-registry.js';
import { ActionPointMetadata } from '../action-point/action-point-metadata.js';
import { HandlerMetadata } from './handler-metadata.js';
import { $logger } from '../../controllers/logger/logger.js';
import { AdapterMatchService } from '../../controllers/adapter/adapter-match-service.js';

class HandlerBuilder {
    constructor(handlerMetadata) {
        this.handlerMetadata = handlerMetadata;
    }
    static from(obj) {
        if (obj instanceof ActionPointMetadata) { // ActionPoint
            return new HandlerBuilder(new HandlerMetadata(obj.target, obj.methodName));
        }
        // Middleware
        return new HandlerBuilder(new HandlerMetadata(obj));
    }
    build() {
        return (runner, skip) => this.invoke(runner, skip);
    }
    middlewareHandler() {
        const provider = MiddlewareRegistry.get(this.handlerMetadata.target);
        /* istanbul ignore next */
        if (!provider) {
            throw new Error(`${nameOf(this.handlerMetadata.target)} middleware component not found in the MiddlewareRegistry`);
        }
        let { instance } = provider;
        if (!instance) {
            instance = InjectorService.get(provider.useClass);
        }
        return (...args) => {
            instance.use.bind(instance);
            return instance.use(...args);
        };
    }
    controlHandler() {
        const provider = ControllerRegistry.get(this.handlerMetadata.target);
        if (!provider) {
            throw new Error('Controller component not found in the ControllerRegistry');
        }
        const target = provider.useClass;
        if (provider.instance === undefined) {
            provider.instance = InjectorService.invoke(target);
        }
        return provider.instance[this.handlerMetadata.methodName].bind(provider.instance);
    }
    getHandler() {
        if (this.handler) {
            return this.handler;
        }
        switch (this.handlerMetadata.type) {
            default:
            case 'function':
                this.handler = this.handlerMetadata.target;
                break;
            case 'middleware':
                this.handler = this.middlewareHandler();
                break;
            case 'controller':
                this.handler = this.controlHandler();
                break;
        }
        return this.handler;
    }
    invoke(runner, skip = false) {
        var _a, _b, _c, _d, _e;
        try {
            const { target, methodName, type } = this.handlerMetadata;
            runner.traceStart(`${target.name} # ${methodName}`, type);
            if (skip) {
                runner.traceEnd(`${target.name} # ${methodName}`, 'skip');
                (_a = runner.skip) === null || _a === void 0 ? void 0 : _a.call(runner);
                return;
            }
            let args = this.runPreHandlers(runner);
            if (type === 'controller') {
                const firstPrehandlerIndex = args.findIndex(v => v !== undefined);
                const rawArgs = runner.getArguments();
                if (firstPrehandlerIndex < 0) {
                    args = rawArgs;
                }
                else {
                    for (let i = firstPrehandlerIndex - 1, j = 0; i >= 0; i -= 1, j += 1) {
                        args[i] = rawArgs[j];
                    }
                }
            }
            let result;
            if (type !== 'middleware'
                || this.adapterMatchService.isAvailable(MiddlewareRegistry.get(target), runner.context.editor)) {
                const handler = this.getHandler();
                result = handler(...args);
            }
            if (result !== undefined && type !== 'function') {
                runner.storeData(result);
            }
            if (!runner.hasCalled) {
                runner.traceEnd(`${target.name} # ${methodName}`, 'success');
                (_b = runner.next) === null || _b === void 0 ? void 0 : _b.call(runner);
            }
            else {
                runner.traceEnd(`${target.name} # ${methodName}`, 'stop');
                (_c = runner.skip) === null || _c === void 0 ? void 0 : _c.call(runner);
            }
        }
        catch (error) {
            const { target, methodName } = this.handlerMetadata;
            $logger.error((_d = error.stack) !== null && _d !== void 0 ? _d : `Error occured at ${target.name} # ${methodName}: ${error}`);
            // next(error);
            runner.traceEnd(`${target.name} # ${methodName}`, 'error');
            (_e = runner.skip) === null || _e === void 0 ? void 0 : _e.call(runner);
        }
    }
    runPreHandlers(runner) {
        const { context } = InjectorService.get(ContextService);
        return this.handlerMetadata.services
            .map(param => { var _a; return (_a = PreHandlers.get(param.service)) === null || _a === void 0 ? void 0 : _a({ context, runner, param }); });
    }
}
__decorate([
    Inject(),
    __metadata("design:type", AdapterMatchService)
], HandlerBuilder.prototype, "adapterMatchService", void 0);

export { HandlerBuilder };
//# sourceMappingURL=handler-builder.js.map
